import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { searchHelp } from "../../../utils/search-help";
import type { HelpCentre } from "@/features/help/types";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The search that opens the help centre: every article's title and summary, matched as the visitor
 * types, with the results right under the field (the head card clips anything that floats). Arrow
 * keys move through them, Enter opens one, Escape clears, and `/` or ⌘K jumps here from anywhere.
 */
export const HelpSearch = ({ centre }: { centre: HelpCentre }) => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const listId = useId();
  const hits = useMemo(() => searchHelp(centre, query), [centre, query]);
  const open = query.trim().length > 0;

  useEffect(() => {
    const focus = (event: KeyboardEvent) => {
      const typing = event.target instanceof HTMLElement && event.target.closest("input, textarea, [contenteditable]");
      if ((event.key === "k" && (event.metaKey || event.ctrlKey)) || (event.key === "/" && !typing)) {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", focus);
    return () => window.removeEventListener("keydown", focus);
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (hits.length ? (current + step + hits.length) % hits.length : 0));
    } else if (event.key === "Enter" && hits[active]) {
      event.preventDefault();
      void navigate({ to: "/help/$slug", params: { slug: hits[active].slug } });
    } else if (event.key === "Escape") {
      setQuery("");
    }
  };

  return (
    <div className="mx-auto mt-9 w-full max-w-[640px] text-left">
      <label className="relative flex h-14 items-center gap-3 rounded-full border border-border bg-background px-5 shadow-[0_18px_40px_-18px_color-mix(in_oklch,var(--foreground)_28%,transparent)] transition-shadow focus-within:ring-2 focus-within:ring-ring">
        <Search aria-hidden className="size-5 shrink-0 text-muted-foreground" />
        <span className="sr-only">{m.help_search_label()}</span>
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && hits[active] ? `${listId}-${active}` : undefined}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={m.help_search_placeholder()}
          className="h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
        />
        <kbd className="hidden h-6 items-center rounded-md border border-border px-1.5 font-sans text-xs text-muted-foreground sm:inline-flex">
          ⌘K
        </kbd>
      </label>

      {open ? (
        <div className="mt-3 overflow-hidden rounded-[24px] border border-border bg-background">
          {hits.length ? (
            <ul id={listId} role="listbox" aria-label={m.help_search_label()} className="divide-y divide-border">
              {hits.map((hit, index) => (
                <li key={hit.slug} id={`${listId}-${index}`} role="option" aria-selected={index === active}>
                  <Link
                    to="/help/$slug"
                    params={{ slug: hit.slug }}
                    onMouseEnter={() => setActive(index)}
                    className={cn(
                      "flex items-center gap-4 px-5 py-3.5 transition-colors",
                      index === active && "bg-[var(--site-wash)]",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-medium">{hit.title}</span>
                      <span className="block truncate text-[13px] text-muted-foreground">
                        {hit.category}
                        {hit.summary ? ` · ${hit.summary}` : ""}
                      </span>
                    </span>
                    <ArrowRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div id={listId} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-[15px]">
              <span className="text-muted-foreground">{m.help_search_none({ query: query.trim() })}</span>
              <Link to="/contact" className="inline-flex items-center gap-1.5 font-medium text-foreground">
                {m.help_search_none_cta()}
                <ArrowRight aria-hidden className="size-4" />
              </Link>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
